import type { EmailReplySendPayload } from '@elva/ui';
import { del, get, getBlob, patch, post, type RequestOptions } from '@/api/client';
import { buildAttachmentDownloadCandidates } from './emailAttachmentDownload';
import {
  encodeMailAttachments,
  uploadEmailAttachmentHandles,
  type MailAttachmentWire,
} from './emailAttachments';
import type {
  EmailAccount,
  EmailAutopilotSettings,
  EmailMessageDetail,
  EmailMessagesResponse,
  LegacyEmailComposePayload,
  LegacyEmailReplyPayload,
  MailAccount,
  MailSendPayload,
  MailSendResponse,
} from './types';

export interface EmailReplyWirePayload {
  body: string;
  html: string;
  attachments?: MailAttachmentWire[];
}

function listFromWire(data: unknown): unknown[] {  if (!data || typeof data !== 'object') return [];

  const obj = data as Record<string, unknown>;
  for (const key of ['accounts', 'results', 'data', 'items']) {
    if (Array.isArray(obj[key])) return obj[key] as unknown[];
  }
  return [];
}

function readNumericAccountId(row: Record<string, unknown>): number | null {
  for (const key of ['id', 'pk', 'account_id', 'mail_account_id', 'legacy_id']) {
    const raw = row[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return Number(raw.trim());
  }
  return null;
}

function readAccountEmail(row: Record<string, unknown>): string {
  for (const key of ['email_address', 'email', 'address', 'from_email']) {
    if (typeof row[key] === 'string') return row[key];
  }
  return '';
}

function normalizeMailAccount(row: unknown): MailAccount | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const id = readNumericAccountId(r);
  if (id == null) return null;

  return {
    id,
    email: readAccountEmail(r),
    status: typeof r.status === 'string' ? r.status : 'active',
  };
}

function normalizeMailAccounts(data: unknown): MailAccount[] {
  return listFromWire(data)
    .map(normalizeMailAccount)
    .filter((row): row is MailAccount => row != null);
}

function normalizeEmailAccount(row: unknown): EmailAccount | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const id =
    typeof r.id === 'string'
      ? r.id
      : typeof r.id === 'number'
        ? String(r.id)
        : null;
  if (!id) return null;

  return {
    id,
    email_address: readAccountEmail(r),
    provider: typeof r.provider === 'string' ? r.provider : '',
    status: typeof r.status === 'string' ? r.status : 'active',
    last_sync_at: typeof r.last_sync_at === 'string' ? r.last_sync_at : null,
  };
}

function normalizeEmailAccounts(data: unknown): EmailAccount[] {
  return listFromWire(data)
    .map(normalizeEmailAccount)
    .filter((row): row is EmailAccount => row != null);
}

export function plainTextToMailHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
  return `<p>${escaped}</p>`;
}

/** Legacy inbox reply — upload attachments first, then POST with handles. */
export async function buildLegacyEmailReplyPayload(
  payload: EmailReplySendPayload,
): Promise<LegacyEmailReplyPayload> {
  const body = payload.body.trim();
  const attachment_handles = await uploadEmailAttachmentHandles(payload.attachments);
  const wire: LegacyEmailReplyPayload = { body };
  if (attachment_handles.length) wire.attachment_handles = attachment_handles;
  return wire;
}

/** FD12 reply/send wire body — text-only; attachments must use legacy reply. */
export async function buildEmailReplyWirePayload(
  payload: EmailReplySendPayload,
): Promise<EmailReplyWirePayload> {
  if (payload.attachments?.length) {
    throw new Error('Use legacy reply when sending attachments.');
  }
  const body = payload.body.trim();
  return {
    body,
    html: plainTextToMailHtml(body),
  };
}

function pickActiveAccount<T extends { status: string }>(accounts: T[]): T | undefined {  return accounts.find((a) => a.status === 'active') ?? accounts[0];
}

/** Prefer FD12 inbox id from send response; Gmail `id` alone cannot load thread detail. */
export function resolveMailSendMessageId(sent: MailSendResponse): string {
  const frontdeskId = sent.frontdesk_message_id?.trim();
  if (frontdeskId) return frontdeskId;

  const messageId = sent.message_id?.trim();
  if (messageId) return messageId;

  if (sent.id != null) return String(sent.id);
  return '';
}

/** Provider thread id for compose follow-up — not used for inbox sync anymore. */
export function resolveMailSendSyncThreadId(sent: MailSendResponse): string | null {
  const threadId = sent.threadId?.trim();
  if (threadId) return threadId;

  if (sent.id != null) {
    const raw = String(sent.id).trim();
    if (raw) return raw;
  }

  return null;
}

export interface MailSendResult {
  messageId: string;
  syncThreadId: string | null;
}

export function resolveMailSendResult(sent: MailSendResponse): MailSendResult {
  return {
    messageId: resolveMailSendMessageId(sent),
    syncThreadId: resolveMailSendSyncThreadId(sent),
  };
}

/** Resolve `account_id` for `POST /mail/send/` from legacy + FD12 account lists. */
export async function resolveSendAccountId(): Promise<number | string> {
  const [mailResult, emailResult] = await Promise.allSettled([
    get<unknown>('/mail/accounts/'),
    get<unknown>('/api/email/accounts'),
  ]);

  if (mailResult.status === 'fulfilled') {
    const mailAccounts = normalizeMailAccounts(mailResult.value);
    const mailAccount = pickActiveAccount(mailAccounts);
    if (mailAccount) return mailAccount.id;
  }

  if (emailResult.status === 'fulfilled') {
    const emailAccounts = normalizeEmailAccounts(emailResult.value);
    const emailAccount = pickActiveAccount(emailAccounts);
    if (emailAccount) {
      const rows = listFromWire(emailResult.value);
      const raw = rows.find((row) => {
        if (!row || typeof row !== 'object') return false;
        const r = row as Record<string, unknown>;
        const wireId =
          typeof r.id === 'string' ? r.id : typeof r.id === 'number' ? String(r.id) : '';
        return wireId === emailAccount.id;
      }) as Record<string, unknown> | undefined;

      const numeric = raw ? readNumericAccountId(raw) : readNumericAccountId({ id: emailAccount.id });
      if (numeric != null) return numeric;
      return emailAccount.id;
    }
  }

  throw mailResult.status === 'rejected'
    ? mailResult.reason
    : emailResult.status === 'rejected'
      ? emailResult.reason
      : new Error('No mailbox account returned from /mail/accounts/ or /api/email/accounts.');
}

async function getAttachmentBlob(
  attachmentId: string,
  messageId?: string,
  threadId?: string,
): Promise<Blob> {
  const candidates = buildAttachmentDownloadCandidates(attachmentId, messageId, threadId);
  if (!candidates.length) {
    throw new Error('Attachment download requires a message id.');
  }

  let lastError: unknown;
  for (const url of candidates) {
    try {
      return await getBlob(url);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError ?? new Error('Attachment download failed.');
}

export const emailApi = {
  getMessages: (
    filters?: {
      needs_review?: boolean;
      status?: string;
      tag?: string;
      search?: string;
      sort?: string;
    },
    options?: RequestOptions,
  ) => get<EmailMessagesResponse>('/api/messages/email', filters ?? {}, options),

  /** FD12 native detail — includes `seen_by` / per-message read receipts. */
  getMessage: (messageId: string, options?: RequestOptions) =>
    get<EmailMessageDetail>(`/api/email/messages/${messageId}`, undefined, options),

  /** Legacy inbox thread detail — includes stored inbound translations. */
  getLegacyThread: (
    threadId: string,
    params?: { translate?: boolean },
    options?: RequestOptions,
  ) => get<EmailMessageDetail>(`/api/messages/email/${threadId}`, params ?? {}, options),

  /** Opt-in inbound → English translation for all messages in the Gmail thread. */
  translateThread: (threadId: string, options?: { force?: boolean }) =>
    post<EmailMessageDetail>(
      `/api/messages/email/${threadId}/translate`,
      options?.force ? { force: true } : {},
    ),

  /** Records staff read receipts for all inbound messages in the thread (FD125). */
  markThreadRead: (providerThreadId: string) =>
    post<void>(`/api/email/threads/${encodeURIComponent(providerThreadId)}/read`),

  /** Legacy inbox reply — returns updated thread detail with outbound message + attachments. */
  sendLegacyReply: (threadId: string, payload: LegacyEmailReplyPayload) =>
    post<EmailMessageDetail>(`/api/messages/email/${threadId}/reply`, payload),

  /** Legacy inbox compose — returns new thread detail. */
  sendLegacyCompose: (payload: LegacyEmailComposePayload) =>
    post<EmailMessageDetail>('/api/messages/email', payload),

  /** FD12 canonical — text-only reply (no upload handles). */
  sendReply: (messageId: string, payload: EmailReplyWirePayload) =>
    post<void>(`/api/email/messages/${messageId}/reply/send`, payload),

  getMailAccounts: async (): Promise<MailAccount[]> => {
    const data = await get<unknown>('/mail/accounts/');
    return normalizeMailAccounts(data);
  },

  sendMail: (payload: MailSendPayload) => post<MailSendResponse>('/mail/send/', payload),

  approveDraft: (messageId: string, payload?: EmailReplyWirePayload) =>
    post<void>(`/api/email/messages/${messageId}/reply/send`, payload ?? {}),

  patchMessage: (messageId: string, action: string) =>
    patch<void>(`/api/messages/email/${messageId}`, { action }),

  /** Pull Gmail updates for one inbox row — `messageId` is the frontdesk UUID (`activeId`). */
  syncThread: (messageId: string) =>
    post<void>(`/api/messages/email/${messageId}/sync`),

  getAttachment: (attachmentId: string, messageId?: string, threadId?: string) =>
    getAttachmentBlob(attachmentId, messageId, threadId),

  markNoReply: (messageId: string, payload?: { reason?: string }) =>
    post<void>(`/api/email/messages/${messageId}/no-reply`, payload ?? {}),

  getAccounts: async (options?: RequestOptions): Promise<EmailAccount[]> => {
    const data = await get<unknown>('/api/email/accounts', undefined, options);
    return normalizeEmailAccounts(data);
  },

  startAccountOauth: (provider: 'gmail' | 'outlook') =>
    get<unknown>('/api/email/accounts/oauth/start', { provider }),

  completeAccountOauth: (payload: { code: string; state: string; provider?: string }) =>
    post<void>('/api/email/accounts/oauth/callback', payload),

  getAutopilot: (options?: RequestOptions) =>
    get<EmailAutopilotSettings>('/api/email/settings/autopilot', undefined, options),
  setAutopilot: (enabled: boolean) =>
    patch<EmailAutopilotSettings>('/api/email/settings/autopilot', {
      email_autopilot_enabled: enabled,
    }),

  disconnectAccount: (accountId: string) =>
    del<void>(`/api/email/accounts/${accountId}`),
};
